import React, { useState } from 'react'
import { resolveImageUrl } from 'utils/imageUrl'

export interface IAppImageProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src' | 'loading'> {
  /** The stored image URL, as it comes from the API (Drive, legacy bucket, anything). */
  src?: string | null
  /**
   * Rendered width in CSS px. Lets a Google Drive image load as a small thumbnail
   * instead of the full photo — set it for avatars and tiles, omit it for full-size views.
   */
  displayWidth?: number
  /** Rendered when there is no `src` or it fails to load (initials, a placeholder icon…). */
  fallback?: React.ReactNode
  loading?: 'lazy' | 'eager'
}

/**
 * Use this instead of a raw `<img>` for ANY image that comes from user data (profile
 * photos, bills, stock images). It resolves the stored URL through `utils/imageUrl` —
 * so Google Drive images work everywhere, and a future storage change is a one-file
 * fix — and falls back cleanly instead of showing a broken image.
 *
 * Passing the URL to a library component instead (web-lib `Avatar`, an `avatar` prop)?
 * Call `resolveImageUrl(url, { width })` yourself.
 */
const AppImage: React.FC<IAppImageProps> = ({
  src,
  displayWidth,
  fallback = null,
  loading = 'lazy',
  alt = '',
  onError,
  ...imgProps
}) => {
  // Remember WHICH url failed, so a new `src` gets a fresh attempt without an effect.
  const [failedUrl, setFailedUrl] = useState<string | null>(null)
  const resolved = resolveImageUrl(src, { width: displayWidth })

  if (!resolved || failedUrl === resolved) {
    return <>{fallback}</>
  }

  return (
    <img
      {...imgProps}
      src={resolved}
      alt={alt}
      loading={loading}
      onError={(event) => {
        setFailedUrl(resolved)
        if (onError) {
          onError(event)
        }
      }}
    />
  )
}

export default AppImage
